import React, { useCallback } from 'react';
import { Checkbox, Divider, FormControlLabel, Grid, TextField, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CircleCheckedFilled from '@mui/icons-material/CheckCircle';
import CircleUnchecked from '@mui/icons-material/RadioButtonUnchecked';
import { partial, times } from 'lodash';
import ImagePicker from '@dyl/components/ImagePicker';
import useChangeEvent from '@dyl/hooks/useChangeEvent';
import useTaskField from '@dyl/pages/Tasks/edit/useTaskField';
import AudioAndTranscriptBlock from '@dyl/blocks/AudioAndTranscriptBlock';
import RubricBlock from '@dyl/blocks/RubricBlock';
import withProps from '@dyl/utils/withProps';
import TaskItems from '@dyl/blocks/TaskItems';

export default withProps(TaskItems, { itemComponent: Item });

const useItemStyles = makeStyles(theme => ({
    answersContainer: {
        marginTop: theme.spacing(6),
        marginBottom: theme.spacing(10),
        marginLeft: theme.spacing(3),
        marginRight: theme.spacing(3),
    },
}));

function Item({ index }) {
    const classes = useItemStyles();
    const useField = partial(useTaskField, 'items', index);

    const [audioId, setAudioId, audioError] = useField('audio_id');
    const [answers] = useField('answers');

    return (
        <>
            <Typography variant='h6'>
                {index === 0 ? 'Example' : `Item ${index}`}
            </Typography>

            {index === 0 && <RubricBlock />}

            <AudioAndTranscriptBlock
                id={`task-item-${index}-stimulus-audio`}
                label='Stimulus Audio'
                value={audioId}
                onChange={id => setAudioId(id)}
                error={audioError}
            />

            <Grid container spacing={8} className={classes.answersContainer}>
                {times(answers?.length, i => (
                    <React.Fragment key={i}>
                        {i > 0 && (
                            <Divider
                                orientation='vertical'
                                flexItem
                            />
                        )}

                        <Grid item xs={12} sm={6} md={answers.length}>
                            <Answer
                                index={i}
                                itemIndex={index}
                            />
                        </Grid>
                    </React.Fragment>
                ))}
            </Grid>
        </>
    );
}

function Answer({ index, itemIndex }) {
    const useField = partial(useTaskField, 'items', itemIndex, 'answers', index);

    const [, setAnswers] = useTaskField('items', itemIndex, 'answers');
    const [isCorrect] = useField('is_correct_answer');
    const [imageId, setImageId, imageError] = useField('image_id');
    const [text, setText, textError] = useField('text');
    const onTextChange = useChangeEvent(setText);

    const letterId = String.fromCharCode('A'.charCodeAt(0) + index);

    const setAsCorrect = useCallback(() => {
        setAnswers(answers => answers.map((a, i) => ({
            ...a,
            is_correct_answer: i === index,
        })));
    }, []);

    return (
        <>
            <Typography variant='subtitle1'>
                Choice {letterId}
            </Typography>

            <ImagePicker
                id={`task-item-${itemIndex}-stimulus-image-option-${index}`}
                label={`Image ${letterId}`}
                value={imageId}
                onChange={setImageId}
                fullWidth
                error={!!imageError}
                helperText={imageError ?? ''}
            />

            <TextField
                id={`task-item-${itemIndex}-stimulus-text-option-${index}`}
                label={`Image ${letterId} accompanying text`}
                variant='filled'
                value={text ?? ''}
                onChange={onTextChange}
                helperText={textError ?? 'Optional'}
                error={!!textError}
                fullWidth
            />

            <FormControlLabel
                label='Key'
                style={{ display: 'flex', flexDirection: 'column', marginTop: 32, marginRight: 0 }}
                control={
                    <Checkbox
                        id={`task-item-${itemIndex}-selectedImage-${index}`}
                        checked={isCorrect}
                        onClick={setAsCorrect}
                        icon={<CircleUnchecked style={{ fontSize: '2.5rem' }}/>}
                        checkedIcon={<CircleCheckedFilled style={{ fontSize: '2.5rem' }}/>}
                        color='primary'
                        style={{ marginRight: 0, marginBottom: 8 }}
                    />
                }
            />
        </>
    );
}